import { FilterIcon2 } from "@/components/Icons/FilterIcon2";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { DynamicSupplyHardwareFiltersResponse } from "@/types";
import { ArrowDownUp, RotateCcwIcon, XIcon } from "lucide-react";
import React, { useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import SelectInput from "@/components/forms/inputs/SelectInput";
import {
  AppliedFilters,
  FilterDropdown,
  FilterKey,
  FilterValues,
  LockedFilterPill,
  buildFilterConfigFromResponse
} from "./filters";
import { SearchBar } from "./SearchBar";
import type { HardwareSortOption } from "../hooks/useSort";
import { TickIcon } from "@/components/Icons/TickIcon";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import { SelectValue } from "@/components/ui/select";
import { CREATE_FORM_STICKY_FILTERS_SLOT_ID } from "@/components/Common/CreateFormStepsNav/CreateFormStepsNav";

/** Render filters inside the sticky steps nav so they never scroll independently. */
function useStickyFiltersSlot(): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    setSlot(document.getElementById(CREATE_FORM_STICKY_FILTERS_SLOT_ID));
  }, []);

  return slot;
}

type FilterControlsProps = {
  searchValue: string;
  onSearchChange: (val: string) => void;
  isSortingEnabled?: boolean;
  sortValue: HardwareSortOption;
  onSortChange: (val: HardwareSortOption) => void;
  // selected filters (API shape)
  filters: Partial<DynamicSupplyHardwareFiltersResponse> | null;
  // available filters/options (from API)
  filtersResponse: DynamicSupplyHardwareFiltersResponse | null;
  onChangeSelected: (next: Partial<DynamicSupplyHardwareFiltersResponse>) => void;
  onApplyFilters: (next?: Partial<DynamicSupplyHardwareFiltersResponse> | null) => void;
  onClearKey: (key: FilterKey) => void;
  onResetFilters: () => void;
  showUnavailable: boolean;
  onShowUnavailableChange: (checked: boolean) => void;
  onTrackApplyFilters?: (filters: Partial<DynamicSupplyHardwareFiltersResponse> | null) => void;
  hiddenFilterKeys?: FilterKey[];
  /** Hide the More filters button/panel without removing the underlying filter UI. */
  hideMoreFilters?: boolean;
  /** Non-clearable pills (e.g. recipe-driven VRAM floor). */
  lockedFilters?: LockedFilterPill[];
};

const quickFilterKeys: FilterKey[] = ["gpuFamily", "regions"];

const HARDWARE_SORT_OPTIONS: { label: string; value: HardwareSortOption }[] = [
  { label: "Recommended", value: "recommended" },
  { label: "Cheaper First", value: "price_asc" },
  { label: "Expensive First", value: "price_desc" },
  { label: "Min VRAM", value: "vram_asc" },
  { label: "Max VRAM", value: "vram_desc" }
];

export const FilterControls: React.FC<FilterControlsProps> = ({
  searchValue,
  onSearchChange,
  isSortingEnabled = false,
  sortValue,
  onSortChange,
  filters,
  filtersResponse,
  onChangeSelected,
  onApplyFilters,
  onClearKey,
  onResetFilters,
  showUnavailable,
  onShowUnavailableChange,
  onTrackApplyFilters,
  hiddenFilterKeys = [],
  hideMoreFilters = false,
  lockedFilters = []
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const showExtendedFilters = !hideMoreFilters && isFilterOpen;
  const stickySlot = useStickyFiltersSlot();
  const config = buildFilterConfigFromResponse(filtersResponse);

  const uiKeyToApiKey: Record<FilterKey, keyof DynamicSupplyHardwareFiltersResponse> = {
    gpuFamily: "gpu_family",
    gpuMemory: "gpu_memory",
    regions: "regions",
    cpuCores: "cpu_cores",
    deviceMemory: "device_memory",
    storage: "storage",
    supplier: "supplier",
    confidentialCompute: "confidential_compute"
  };

  // API provides GB values; support parsing TB/GB/MB labels into GB
  const parseSizeLabelToGb = (label: string) => {
    const lower = label.toLowerCase().trim();
    if (lower.endsWith("tb")) {
      const tb = parseFloat(lower.replace("tb", "").trim());
      return isNaN(tb) ? 0 : tb * 1024;
    }
    if (lower.endsWith("mb")) {
      const mb = parseFloat(lower.replace("mb", "").trim());
      return isNaN(mb) ? 0 : mb / 1024;
    }
    if (lower.endsWith("gb")) {
      const gb = parseFloat(lower.replace("gb", "").trim());
      return isNaN(gb) ? 0 : gb;
    }
    const n = Number(label);
    return isNaN(n) ? 0 : n;
  };

  const formatCapacity = (gb: number) => {
    if (typeof gb !== "number" || isNaN(gb)) return "-";
    if (gb >= 1024) {
      const tb = gb / 1024;
      const value = tb % 1 === 0 ? tb.toFixed(0) : tb.toFixed(1);
      return `${parseFloat(value)} TB`;
    }
    return `${gb} GB`;
  };

  // Build UI values from API-shaped selected filters
  const toUiFilterValues = (): FilterValues => {
    const vals: FilterValues = {
      gpuFamily: [],
      gpuMemory: [],
      regions: [],
      cpuCores: [],
      deviceMemory: [],
      storage: [],
      supplier: [],
      confidentialCompute: []
    };
    if (!filters) return vals;
    if (filters.gpu_family?.length) {
      vals.gpuFamily = filters.gpu_family as unknown as string[];
    }
    if (filters.gpu_memory?.min || filters.gpu_memory?.max) {
      const a = filters.gpu_memory?.min as unknown as number | undefined;
      const b = filters.gpu_memory?.max as unknown as number | undefined;
      vals.gpuMemory =
        typeof a === "number" || typeof b === "number"
          ? [`${formatCapacity(a ?? 0)} - ${formatCapacity(b ?? 0)}`]
          : [];
    }
    if (filters.regions?.length) {
      vals.regions = filters.regions as unknown as string[];
    }
    if (filters.cpu_cores?.min !== undefined || filters.cpu_cores?.max !== undefined) {
      const a = filters.cpu_cores?.min;
      const b = filters.cpu_cores?.max;
      vals.cpuCores =
        typeof a === "number" || typeof b === "number" ? [`${a ?? 0} - ${b ?? 0} cores`] : [];
    }
    if (filters.device_memory?.min || filters.device_memory?.max) {
      const a = filters.device_memory?.min as unknown as number | undefined;
      const b = filters.device_memory?.max as unknown as number | undefined;
      vals.deviceMemory =
        typeof a === "number" || typeof b === "number"
          ? [`${formatCapacity(a ?? 0)} - ${formatCapacity(b ?? 0)}`]
          : [];
    }
    if (filters.storage?.min || filters.storage?.max) {
      const a = filters.storage?.min as unknown as number | undefined;
      const b = filters.storage?.max as unknown as number | undefined;
      vals.storage =
        typeof a === "number" || typeof b === "number"
          ? [`${formatCapacity(a ?? 0)} - ${formatCapacity(b ?? 0)}`]
          : [];
    }
    if (Array.isArray(filters.supplier) && filters.supplier.length > 0) {
      vals.supplier = filters.supplier as unknown as string[];
    }
    if (filters.confidential_compute?.length) {
      vals.confidentialCompute = filters.confidential_compute.map(String);
    }
    return vals;
  };

  const uiFilters = toUiFilterValues();

  const quickFilterHiddenKeys = useMemo<FilterKey[]>(() => {
    const hideSet = new Set<FilterKey>([
      ...hiddenFilterKeys,
      "gpuMemory",
      "cpuCores",
      "deviceMemory",
      "storage",
      "supplier",
      "confidentialCompute"
    ]);
    quickFilterKeys.forEach((key) => hideSet.delete(key));
    return Array.from(hideSet);
  }, [hiddenFilterKeys]);

  const extendedFilterHiddenKeys = useMemo<FilterKey[]>(
    () => [...hiddenFilterKeys, ...quickFilterKeys],
    [hiddenFilterKeys]
  );

  const hasActiveFilters =
    showUnavailable || Object.values(uiFilters).some((value) => value.length > 0);

  const handleChangeValues = (key: FilterKey, values: string[]) => {
    const next = { ...(filters || {}) } as Partial<DynamicSupplyHardwareFiltersResponse>;
    const apiKey = uiKeyToApiKey[key];
    const nextMutable = next as Record<string, unknown>;
    switch (key) {
      case "gpuFamily":
        nextMutable[apiKey] = values;
        break;
      case "gpuMemory": {
        const minGb = Math.max(...values.map(parseSizeLabelToGb), 0);
        const max = filtersResponse?.gpu_memory?.max;
        nextMutable[apiKey] = { min: minGb, max: max ?? minGb };
        break;
      }
      case "regions":
        nextMutable[apiKey] = values;
        break;
      case "cpuCores": {
        const min = Math.max(...values.map((v) => parseInt(v, 10)), 0);
        const max = filtersResponse?.cpu_cores?.max;
        nextMutable[apiKey] = { min, max: max ?? min };
        break;
      }
      case "deviceMemory": {
        const minGb = Math.max(...values.map(parseSizeLabelToGb), 0);
        const max = filtersResponse?.device_memory?.max;
        nextMutable[apiKey] = { min: minGb, max: max ?? minGb };
        break;
      }
      case "storage": {
        const minGb = Math.max(...values.map(parseSizeLabelToGb), 0);
        const max = filtersResponse?.storage?.max;
        nextMutable[apiKey] = { min: minGb, max: max ?? minGb };
        break;
      }
      case "supplier":
        nextMutable[apiKey] = values;
        break;
      case "confidentialCompute":
        nextMutable[apiKey] = values.map((v) => v === "true");
        break;
    }
    onChangeSelected(next);
    if (quickFilterKeys.includes(key)) {
      const appliedFilters = (next || null) as Partial<DynamicSupplyHardwareFiltersResponse> | null;
      onTrackApplyFilters?.(appliedFilters);
      onApplyFilters(appliedFilters);
    }
  };

  const filtersContent = (
    <div className="flex flex-col gap-2 px-6 pb-4 max-lg:w-full lg:px-8">
      <SearchBar value={searchValue} onChange={onSearchChange} />

      <div className="flex items-center gap-2 max-lg:w-full">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <FilterDropdown
            config={config}
            filters={uiFilters}
            onChangeValues={handleChangeValues}
            selectedApi={filters}
            availableApi={filtersResponse}
            onChangeSelected={onChangeSelected}
            hiddenFilterKeys={quickFilterHiddenKeys}
            className="flex-1"
            itemClassName="flex-1"
          />
          {isSortingEnabled && (
            <SelectInput
              name="hardwareSort"
              value={sortValue}
              onChange={(v) => {
                const next = v as HardwareSortOption;
                if (next === sortValue) return;
                const option = HARDWARE_SORT_OPTIONS.find((o) => o.value === next);
                if (option) {
                  trackEvent(AnalyticsEventType.GPU_SORTED_SELECTED, { value: option.label });
                }
                onSortChange(next);
              }}
              options={HARDWARE_SORT_OPTIONS}
              triggerClassName="dark:bg-gray-dark-600"
              className="w-[180px] shrink-0"
              renderValue={(val) => {
                const label = HARDWARE_SORT_OPTIONS.find((o) => o.value === val)?.label ?? "";
                return (
                  <SelectValue key={val}>
                    <span className="flex min-w-0 flex-1 items-center gap-2 text-gray-dark-2200">
                      <ArrowDownUp className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="truncate">{label}</span>
                    </span>
                  </SelectValue>
                );
              }}
            />
          )}
        </div>
        <label className="flex h-10 cursor-pointer items-center gap-2 rounded-[8px] border border-border bg-background px-3 text-xs font-medium text-foreground shadow-[-8px_8px_8px_0px_rgba(0,0,0,0.02)] hover:bg-gray-light-500 dark:hover:bg-border">
          <Switch
            checked={showUnavailable}
            onCheckedChange={onShowUnavailableChange}
            aria-label="Show unavailable inventory"
          />
          Show unavailable
        </label>
        {!hideMoreFilters && (
          <SimpleTooltip
            content={showExtendedFilters ? "Apply filters" : "More filters"}
            id="apply-filters"
          >
            <Button
              variant={showExtendedFilters ? "primary" : "outline"}
              className={cn(
                "gap-2 px-3 py-2 ",
                !showExtendedFilters && "[&_svg]:hover:text-foreground"
              )}
              onClick={() => {
                if (isFilterOpen) {
                  onTrackApplyFilters?.(
                    (filters || null) as Partial<DynamicSupplyHardwareFiltersResponse> | null
                  );
                  onApplyFilters(
                    (filters || null) as Partial<DynamicSupplyHardwareFiltersResponse> | null
                  );
                  setIsFilterOpen(false);
                } else {
                  trackEvent(AnalyticsEventType.MORE_FILTERS_CLICKED);
                  setIsFilterOpen(true);
                }
              }}
            >
              {showExtendedFilters ? (
                <TickIcon className="h-5 w-5 " />
              ) : (
                <FilterIcon2 className="h-5 w-5 " />
              )}
            </Button>
          </SimpleTooltip>
        )}
        {hasActiveFilters && (
          <SimpleTooltip content="Reset filters" id="reset-filters">
            <Button
              variant="outline"
              className="gap-2 px-3 py-2 [&_svg]:hover:text-foreground"
              onClick={onResetFilters}
            >
              <RotateCcwIcon className="h-5 w-5 " />
            </Button>
          </SimpleTooltip>
        )}
        {showExtendedFilters && (
          <SimpleTooltip content="Close filters" id="close-filters">
            <Button
              variant="outline"
              className="gap-2 px-3 py-2 [&_svg]:hover:text-foreground"
              onClick={() => {
                setIsFilterOpen(false);
                trackEvent(AnalyticsEventType.MORE_FILTERS_CLOSED);
              }}
            >
              <XIcon className="h-5 w-5 " />
            </Button>
          </SimpleTooltip>
        )}
      </div>

      {showExtendedFilters && (
        <FilterDropdown
          config={config}
          filters={uiFilters}
          onChangeValues={handleChangeValues}
          selectedApi={filters}
          availableApi={filtersResponse}
          onChangeSelected={onChangeSelected}
          hiddenFilterKeys={extendedFilterHiddenKeys}
        />
      )}

      {!showExtendedFilters && (
        <AppliedFilters
          config={config}
          filters={uiFilters}
          onClearKey={onClearKey}
          lockedFilters={lockedFilters}
        />
      )}
    </div>
  );

  if (stickySlot) {
    return createPortal(filtersContent, stickySlot);
  }

  return filtersContent;
};
